import "twin.macro";

import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";

import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";

import { OpaqueCard } from "@components/StyledComponents";
import { useGroupCategoriesListQuery } from "@features/groupCategories";
import { ItemPreviewImageButton, variantName } from "@features/items";
import IndexTable, { PaginatedResourceProps } from "@features/ui/indexTable";
import { insertIf } from "@features/ui/indexTable/helpers";
import { PurchaseOrder, PurchaseOrderVariant } from "@models";
import { Variant } from "@models/Variant";
import {
  formatDateString,
  formatMoneyString,
  formatUtcDate,
} from "@utility/utilityFunctions";

import { purchaseOrdersKeyFactory } from "../data";
import { usePurchaseOrderVariantsListQuery } from "../data/purchaseOrderVariantQueries";
import { poTypeLabel } from "../helpers";
import { calcTotalCostPurchaseOrder } from "./purchasingHelpers";

const CollapsedVariantTable = ({
  purchaseOrderId,
}: {
  purchaseOrderId: string;
}) => {
  const { data = [], ...rest } =
    usePurchaseOrderVariantsListQuery(purchaseOrderId);

  const itemRows = data.filter((pov) => !pov.isSetupFee);
  const { data: groupCategories = [] } = useGroupCategoriesListQuery();

  return (
    <OpaqueCard tw="p-0 max-w-fit w-full relative">
      <IndexTable
        tableSize="small"
        rows={itemRows}
        columns={[
          {
            id: "variant",
            label: "",
            render: (variant: Variant) => (
              <ItemPreviewImageButton
                partialItem={variant.item}
                imageId={variant.imageId}
                tw="w-14 h-14"
              />
            ),
          },
          {
            id: "_sku",
            label: "SKU",
            render: (_, pov) =>
              pov.variant.externalWarehouseId ?? pov.variant.variantSku,
          },
          {
            id: "variant.item.name",
            label: "Item Name",
          },
          {
            id: "_variantNames",
            label: "Variant Names",
            render: (_, pov) => variantName(pov.variant),
          },
          ...groupCategories.map((gc, i) => ({
            id: `_groupCategory-${i}`,
            label: gc.name,
            render: (_, pov: PurchaseOrderVariant) =>
              !pov.isSetupFee
                ? pov.variant.item.groups.find(
                    (group) => group.groupCategory.id === gc.id
                  )?.name
                : "",
          })),
          {
            id: "qty",
            label: "Qty",
          },
          {
            id: "actualCost",
            label: "Actual Cost",
            align: "right",
            render: (actualCost) => formatMoneyString(actualCost),
          },
          {
            id: "_total",
            label: "Extended Cost",
            align: "right",
            render: (_, pov) => formatMoneyString(+pov.actualCost * pov.qty),
          },
        ]}
        {...rest}
      />
    </OpaqueCard>
  );
};
const PurchaseOrderHistoryTable = ({
  rows,
  ...tableProps
}: PaginatedResourceProps & {
  rows: PurchaseOrder[];
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleRowClick = (id) => {
    navigate(`/purchase-orders/history/${id}`, {
      state: { search: location.search },
    });
  };

  const {
    role,
    organization: { hasPurchasingAgents, hasBeacons },
  } = useSelector((state: any) => state.currentUser);

  return (
    <OpaqueCard tw="p-0 overflow-hidden relative">
      <IndexTable
        rows={rows}
        columns={[
          { id: "id", label: "PO #", sort: "id", reverseSort: true },
          {
            id: "type",
            label: "PO Type",
            sort: "type",
            render: (v) => <span tw="whitespace-nowrap">{poTypeLabel(v)}</span>,
          },
          ...insertIf(role !== "supplier", {
            id: "supplier.name",
            label: "Supplier",
            sort: "supplier-name",
            className: "whitespace-nowrap",
          }),
          {
            id: "supplierReference",
            label: "Supplier Reference",
          },
          {
            id: "invoiceNumber",
            label: "Invoice #",
          },
          ...insertIf(hasPurchasingAgents, {
            id: "purchasingAgentReferenceNumber",
            label: "External PO #",
          }),
          {
            id: "purchaserName",
            label: "Purchaser",
            sort: "purchaser-name",
          },
          {
            id: "programNames",
            label: "Program",
            render: (p) => <div tw="min-w-40 line-clamp-2">{p}</div>,
          },
          {
            id: "totalProductCost",
            label: "Total Product Cost",
            align: "right",
            render: formatMoneyString,
          },
          {
            id: "totalSetupFeesCost",
            label: "Total Setup Fees",
            align: "right",
            render: formatMoneyString,
          },
          {
            id: "totalTaxCost",
            label: "Total Tax",
            align: "right",
            render: formatMoneyString,
          },
          {
            id: "totalFreightCost",
            label: "Total Freight",
            align: "right",
            render: formatMoneyString,
          },
          {
            id: "_total",
            label: "Grand Total",
            align: "right",
            render: (v, purchaseOrder) =>
              formatMoneyString(calcTotalCostPurchaseOrder(purchaseOrder)),
          },
          {
            id: "status",
            label: "Status",
            sort: "status",
            render: _.startCase,
            className: "whitespace-nowrap",
          },
          {
            id: "submittedAt",
            label: "Submitted Date",
            sort: "submitted-at",
            reverseSort: true,
            render: (submittedAt) => formatUtcDate(submittedAt),
          },
          {
            id: "inMarketDate",
            label: "In Market Date",
            sort: "in-market-date",
            reverseSort: true,
            render: formatDateString,
          },
          ...insertIf(hasBeacons, {
            id: "hasBeacons",
            label: "Has Beacons",
            render: (v) => (v ? "Yes" : "No"),
          }),
          { id: "shipmentCount", label: "Shipment Count" },
        ]}
        onRowClick={(row) => {
          queryClient.setQueryData<PurchaseOrder>(
            purchaseOrdersKeyFactory.detail(row.id).queryKey,
            row
          );
          handleRowClick(row.id);
        }}
        collapsibleContent={(row) => {
          return <CollapsedVariantTable purchaseOrderId={row.id} />;
        }}
        {...tableProps}
      />
    </OpaqueCard>
  );
};

export default PurchaseOrderHistoryTable;
